<template>
    <PageLoading v-if="isLoading" />
    <div v-else class="main-wrap sign-up-page">
        <div class="sign-up-form column q-col-gutter-y-md justify-center">
            <h1 class="capitalize-text">{{ $t('actions.signup') }}</h1>
            <div class="tagline">
                {{ $t('signup.register_to_start_learning') }}
            </div>
            <q-stepper
                ref="stepper"
                v-model="step"
                animated
                vertical
                flat
                class="sign-up-stepper"
            >
                <!-- step 1: type of client -->
                <q-step
                    :name="1"
                    :title="$t('signup.type_of_user')"
                    icon="manage_accounts"
                    :done="step > 1"
                >
                    <div class="q-pa-lg">
                        <div class="col">
                            <q-option-group
                                v-model="userType"
                                :options="userTypeOptions"
                                color="primary"
                                @input="resetDataAndValidations"
                            />
                        </div>
                    </div>
                </q-step>
                <!-- step 2: user form -->
                <q-step
                    :name="2"
                    :title="$t('signup.user_data')"
                    icon="person"
                    :done="step > 2"
                >
                    <q-form autocorrect="off" autocomplete="off">
                        <div class="row q-col-gutter-lg q-mt-md">
                            <!-- first name -->
                            <div class="col-6">
                                <q-input
                                    v-model="user.first_name"
                                    class="required"
                                    name="first_name"
                                    :label="$t('general.name')"
                                    type="text"
                                    bg-color="black"
                                    filled
                                    clearable
                                    counter
                                    maxlength="35"
                                    :error-message="firstNameErrors"
                                    :error="firstNameErrors != ''"
                                    @input="$v.user.first_name.$touch()"
                                    @blur="$v.user.first_name.$touch()"
                                >
                                </q-input>
                            </div>
                            <!-- tax number -->
                            <div
                                :class="{
                                    'col-3': userType === 'student',
                                    'col-6': userType !== 'student',
                                }"
                            >
                                <q-input
                                    v-model="user.tax_number"
                                    class="required"
                                    name="tax_number"
                                    :label="$t('general.tax_number')"
                                    type="text"
                                    bg-color="black"
                                    filled
                                    clearable
                                    counter
                                    maxlength="15"
                                    :error-message="taxNumberErrors"
                                    :error="taxNumberErrors != ''"
                                    @input="$v.user.tax_number.$touch()"
                                    @blur="$v.user.tax_number.$touch()"
                                />
                            </div>
                            <!-- currency -->
                            <div v-if="userType === 'student'" class="col-3">
                                <q-select
                                    v-model="selectedCurrency"
                                    class="required"
                                    :label="$t('general.currency')"
                                    bg-color="black"
                                    :options="currencyOptions"
                                    option-value="id"
                                    option-label="name"
                                    autocomplete="void"
                                    clearable
                                    :error-message="currencyErrors"
                                    :error="currencyErrors != ''"
                                    @input="$v.selectedCurrency.$touch()"
                                    @blur="$v.selectedCurrency.$touch()"
                                >
                                    <template
                                        v-if="selectedCurrency"
                                        v-slot:selected
                                    >
                                        <span class="text-uppercase text-white">
                                            {{ selectedCurrency.key }}</span
                                        >
                                    </template>
                                    <template v-slot:option="scope">
                                        <q-item
                                            v-bind="scope.itemProps"
                                            v-on="scope.itemEvents"
                                        >
                                            <q-item-section>
                                                <q-item-label
                                                    class="text-capitalize"
                                                    >{{ scope.opt.name }}
                                                    <span class="text-uppercase"
                                                        >({{
                                                            scope.opt.key
                                                        }})</span
                                                    >
                                                </q-item-label>
                                            </q-item-section>
                                        </q-item>
                                    </template>
                                </q-select>
                            </div>
                            <!-- country -->
                            <div class="col-6">
                                <q-select
                                    v-model="user.country_id"
                                    class="required"
                                    :label="$t('general.country')"
                                    bg-color="black"
                                    hide-selected
                                    input-debounce="300"
                                    :options="filteredCountries"
                                    option-value="id"
                                    :option-label="(option) => option.name"
                                    map-options
                                    emit-value
                                    use-input
                                    fill-input
                                    clearable
                                    autocomplete="void"
                                    :error-message="countryErrors"
                                    :error="countryErrors != ''"
                                    @input="$v.user.country_id.$touch()"
                                    @blur="$v.user.country_id.$touch()"
                                    @filter="filterCountries"
                                >
                                </q-select>
                            </div>
                            <!-- street -->
                            <div class="col-6">
                                <q-input
                                    v-model="user.street"
                                    class="required"
                                    name="street"
                                    :label="$t('general.street')"
                                    bg-color="black"
                                    filled
                                    clearable
                                    counter
                                    maxlength="35"
                                    :error-message="streetErrors"
                                    :error="streetErrors != ''"
                                    @input="$v.user.street.$touch()"
                                    @blur="$v.user.street.$touch()"
                                />
                            </div>
                            <!-- city -->
                            <div class="col-6">
                                <q-input
                                    v-model="user.city"
                                    class="required"
                                    name="city"
                                    :label="$t('general.city')"
                                    bg-color="black"
                                    filled
                                    clearable
                                    counter
                                    maxlength="100"
                                    :error-message="cityErrors"
                                    :error="cityErrors != ''"
                                    @input="$v.user.city.$touch()"
                                    @blur="$v.user.city.$touch()"
                                />
                            </div>
                            <!-- state -->
                            <div class="col-6">
                                <q-input
                                    v-model="user.state"
                                    class="required"
                                    name="state"
                                    :label="$t('general.state')"
                                    bg-color="black"
                                    filled
                                    clearable
                                    counter
                                    maxlength="50"
                                    :error-message="stateErrors"
                                    :error="stateErrors != ''"
                                    @input="$v.user.state.$touch()"
                                    @blur="$v.user.state.$touch()"
                                />
                            </div>
                            <!-- zip code -->
                            <div class="col-6">
                                <q-input
                                    v-model="user.zip_code"
                                    name="zip_code"
                                    :label="$t('general.zip_code')"
                                    bg-color="black"
                                    filled
                                    clearable
                                    counter
                                    maxlength="20"
                                />
                            </div>
                            <!-- email -->
                            <div class="col-6">
                                <q-input
                                    v-model.trim="user.email"
                                    class="required"
                                    name="email"
                                    :label="$t('general.email')"
                                    type="email"
                                    bg-color="black"
                                    filled
                                    clearable
                                    counter
                                    maxlength="250"
                                    :error-message="emailErrors"
                                    :error="emailErrors != ''"
                                    @input="$v.user.email.$touch()"
                                    @blur="$v.user.email.$touch()"
                                />
                            </div>
                            <!-- password -->
                            <div class="col-6">
                                <q-input
                                    v-model="user.password"
                                    class="required"
                                    name="password"
                                    :label="$t('general.password')"
                                    :type="isPassword ? 'password' : 'text'"
                                    bg-color="black"
                                    filled
                                    clearable
                                    :error-message="passwordErrors"
                                    :error="passwordErrors != ''"
                                    @input="$v.user.password.$touch()"
                                    @blur="$v.user.password.$touch()"
                                >
                                    <template v-slot:append>
                                        <q-icon
                                            class="cursor-pointer"
                                            :name="
                                                isPassword
                                                    ? 'visibility_off'
                                                    : 'visibility'
                                            "
                                            @click="isPassword = !isPassword"
                                        />
                                    </template>
                                </q-input>
                            </div>
                            <!-- password confirm -->
                            <div class="col-6">
                                <q-input
                                    v-model="user.passwordConfirm"
                                    class="required q-mb-md"
                                    name="password_confirm"
                                    :label="$t('general.password_confirm')"
                                    :type="
                                        isPasswordConfirm ? 'password' : 'text'
                                    "
                                    bg-color="black"
                                    filled
                                    clearable
                                    :error-message="passwordConfirmErrors"
                                    :error="passwordConfirmErrors != ''"
                                    @input="$v.user.passwordConfirm.$touch()"
                                    @blur="$v.user.passwordConfirm.$touch()"
                                >
                                    <template v-slot:append>
                                        <q-icon
                                            class="cursor-pointer"
                                            :name="
                                                isPasswordConfirm
                                                    ? 'visibility_off'
                                                    : 'visibility'
                                            "
                                            @click="
                                                isPasswordConfirm = !isPasswordConfirm
                                            "
                                        />
                                    </template>
                                </q-input>
                            </div>
                        </div>
                    </q-form>
                </q-step>
                <!-- step 3: corporate form -->
                <q-step
                    v-if="userType == 'client'"
                    :name="3"
                    :title="$t('signup.company_data')"
                    icon="business"
                    :done="step > 3"
                >
                    <div class="sign-up-form">
                        <q-form autocorrect="off" autocomplete="off">
                            <div class="row q-col-gutter-lg q-mt-md">
                                <!-- company name -->
                                <div class="col-12">
                                    <q-input
                                        v-model="company.title"
                                        name="complany_title"
                                        class="q-mt-md required"
                                        :label="$t('general.company_name')"
                                        bg-color="black"
                                        filled
                                        clearable
                                        counter
                                        maxlength="35"
                                        :error-message="companyTitleErrors"
                                        :error="companyTitleErrors != ''"
                                        @input="$v.company.title.$touch()"
                                        @blur="$v.company.title.$touch()"
                                    />
                                </div>
                                <!-- email -->
                                <div class="col-6">
                                    <q-input
                                        v-model.trim="company.email"
                                        class="required"
                                        name="email"
                                        :label="$t('general.email')"
                                        type="email"
                                        bg-color="black"
                                        filled
                                        clearable
                                        :error-message="companyEmailErrors"
                                        :error="companyEmailErrors != ''"
                                        @input="$v.company.email.$touch()"
                                        @blur="$v.company.email.$touch()"
                                    />
                                </div>
                                <!-- phone number -->
                                <div class="col-6">
                                    <q-input
                                        v-model="company.phone"
                                        class="required"
                                        name="phone"
                                        :label="$t('general.phone')"
                                        bg-color="black"
                                        filled
                                        clearable
                                        counter
                                        maxlength="25"
                                        :error-message="companyPhoneErrors"
                                        :error="companyPhoneErrors != ''"
                                        @input="$v.company.phone.$touch()"
                                        @blur="$v.company.phone.$touch()"
                                    />
                                </div>
                                <!-- tax number -->
                                <div
                                    :class="{
                                        'col-3': userType === 'client',
                                        'col-6': userType !== 'client',
                                    }"
                                >
                                    <q-input
                                        v-model="company.tax_number"
                                        class="required"
                                        name="tax_number"
                                        :label="$t('general.tax_number')"
                                        bg-color="black"
                                        filled
                                        clearable
                                        counter
                                        maxlength="15"
                                        :error-message="companyTaxNumberErrors"
                                        :error="companyTaxNumberErrors != ''"
                                        @input="$v.company.tax_number.$touch()"
                                        @blur="$v.company.tax_number.$touch()"
                                    />
                                </div>
                                <!-- currency -->
                                <div v-if="userType === 'client'" class="col-3">
                                    <q-select
                                        v-model="selectedCurrency"
                                        class="required"
                                        :label="$t('general.currency')"
                                        bg-color="black"
                                        :options="currencyOptions"
                                        option-value="id"
                                        option-label="name"
                                        autocomplete="void"
                                        clearable
                                        :error-message="currencyErrors"
                                        :error="currencyErrors != ''"
                                        @input="$v.selectedCurrency.$touch()"
                                        @blur="$v.selectedCurrency.$touch()"
                                    >
                                        <template
                                            v-if="selectedCurrency"
                                            v-slot:selected
                                        >
                                            <span
                                                class="text-uppercase text-white"
                                            >
                                                {{ selectedCurrency.key }}</span
                                            >
                                        </template>
                                        <template v-slot:option="scope">
                                            <q-item
                                                v-bind="scope.itemProps"
                                                v-on="scope.itemEvents"
                                            >
                                                <q-item-section>
                                                    <q-item-label
                                                        class="text-capitalize"
                                                        >{{ scope.opt.name }}
                                                        <span
                                                            class="text-uppercase"
                                                            >({{
                                                                scope.opt.key
                                                            }})</span
                                                        >
                                                    </q-item-label>
                                                </q-item-section>
                                            </q-item>
                                        </template>
                                    </q-select>
                                </div>
                                <!-- country -->
                                <div class="col-6">
                                    <q-select
                                        v-model="company.country_id"
                                        class="required"
                                        :label="$t('general.choose')"
                                        bg-color="black"
                                        hide-selected
                                        input-debounce="300"
                                        :options="filteredCompanyCountries"
                                        option-value="id"
                                        :option-label="(option) => option.name"
                                        map-options
                                        emit-value
                                        use-input
                                        fill-input
                                        autocomplete="void"
                                        :error-message="countryCompanyErrors"
                                        :error="countryCompanyErrors != ''"
                                        @input="$v.company.country_id.$touch()"
                                        @blur="$v.company.country_id.$touch()"
                                        @filter="filterCompanyCountries"
                                    >
                                        <template
                                            v-if="company.country_id"
                                            v-slot:append
                                        >
                                            <q-icon
                                                class="cursor-pointer"
                                                name="cancel"
                                                @click.stop.prevent="
                                                    company.country_id = ''
                                                "
                                            />
                                        </template>
                                    </q-select>
                                </div>
                                <!-- street -->
                                <div class="col-6">
                                    <q-input
                                        v-model="company.street"
                                        class="required"
                                        name="street"
                                        :label="$t('general.street')"
                                        bg-color="black"
                                        filled
                                        clearable
                                        counter
                                        maxlength="35"
                                        :error-message="companyStreetErrors"
                                        :error="companyStreetErrors != ''"
                                        @input="$v.company.street.$touch()"
                                        @blur="$v.company.street.$touch()"
                                    />
                                </div>
                                <!-- city -->
                                <div class="col-6">
                                    <q-input
                                        v-model="company.city"
                                        class="required"
                                        name="city"
                                        :label="$t('general.city')"
                                        bg-color="black"
                                        filled
                                        clearable
                                        counter
                                        maxlength="100"
                                        :error-message="companyCityErrors"
                                        :error="companyCityErrors != ''"
                                        @input="$v.company.city.$touch()"
                                        @blur="$v.company.city.$touch()"
                                    />
                                </div>
                                <!-- state -->
                                <div class="col-6">
                                    <q-input
                                        v-model="company.state"
                                        class="required"
                                        name="state"
                                        :label="$t('general.state')"
                                        bg-color="black"
                                        filled
                                        clearable
                                        counter
                                        maxlength="35"
                                        :error-message="companyStateErrors"
                                        :error="companyStateErrors != ''"
                                        @input="$v.company.state.$touch()"
                                        @blur="$v.company.state.$touch()"
                                    />
                                </div>
                                <!-- zip code -->
                                <div class="col-6">
                                    <q-input
                                        v-model="company.zip_code"
                                        name="zip_code"
                                        :label="$t('general.zip_code')"
                                        bg-color="black"
                                        filled
                                        clearable
                                        counter
                                        maxlength="20"
                                    />
                                </div>
                            </div>
                        </q-form>
                    </div>
                </q-step>
                <!-- stepper navigation -->
                <template v-slot:navigation>
                    <q-stepper-navigation>
                        <div class="row justify-center q-mb-md">
                            <p v-if="step > 1" class="text-primary">
                                Os campos assinalados com * são de preenchimento
                                obrigatório.
                            </p>
                        </div>
                        <div class="row stepper-navigation-buttons">
                            <div v-if="step > 1" class="col q-px-sm">
                                <q-btn
                                    class="sign-up-btn"
                                    text-color="white"
                                    flat
                                    :label="$t('actions.previous')"
                                    @click="$refs.stepper.previous()"
                                />
                            </div>
                            <div v-if="!lastStep" class="col q-px-sm">
                                <q-btn
                                    class="sign-up-btn"
                                    text-color="white"
                                    flat
                                    :disabled="userInvalid"
                                    :label="$t('actions.next')"
                                    @click="$refs.stepper.next()"
                                />
                            </div>
                            <div v-if="lastStep" class="col q-px-sm">
                                <q-btn
                                    class="sign-up-btn"
                                    text-color="white"
                                    flat
                                    :loading="isSigningUp"
                                    :label="$t('actions.signup')"
                                    @click="signUp"
                                />
                            </div>
                        </div>
                    </q-stepper-navigation>
                </template>
            </q-stepper>
        </div>
    </div>
</template>

<script>
import validations from '@/views/authentication/mixins/validations'
import PageLoading from '@/components/PageLoading'
import authenticationService from '@/services/authentication'
import countriesService from '@/services/modules/countries'
import currenciesService from '@/services/modules/currencies'
// import store from '@/store'

const loadingKeys = {
    fetchingPage: 'fetching-page',
    signingUp: 'signing-up',
    fetchingCountries: 'fetching-countries',
    fetchingCurrencies: 'fetching-currencies',
}

export default {
    name: 'Signup',

    components: {
        PageLoading,
    },

    mixins: [validations],

    data() {
        return {
            isPassword: true,
            isPasswordConfirm: true,

            step: 1,

            userType: 'student',

            user: {
                first_name: '',
                last_name: '',
                tax_number: '',
                phone: '',
                street: '',
                city: '',
                state: '',
                zip_code: '',
                email: '',
                password: '',
                passwordConfirm: '',
                country_id: null,
            },

            company: {
                title: '',
                tax_number: '',
                phone: '',
                email: '',
                street: '',
                city: '',
                state: '',
                zip_code: '',
                country_id: null,
            },

            selectedCurrency: null,

            countries: [],
            filteredCountries: [],
            filteredCompanyCountries: [],

            currencyOptions: [],
        }
    },

    computed: {
        userTypeOptions() {
            return [
                {
                    label: this.$t('signup.individual'),
                    value: 'student',
                },
                {
                    label: this.$t('signup.corporate'),
                    value: 'client',
                },
            ]
        },

        isLoading() {
            return this.isSigningUp
        },

        isSigningUp() {
            return (
                this.loadings.filter(
                    (loading) => loading.key === loadingKeys.signingUp
                ).length > 0
            )
        },

        lastStep() {
            return (
                (this.step === 3) & (this.userType == 'client') ||
                (this.step === 2) & (this.userType == 'student')
            )
        },

        userInvalid() {
            return this.step > 1 ? this.$v.user.$invalid : false
        },
    },

    async created() {
        this.fetchCountries()
        this.fetchCurrencies()
    },

    methods: {
        filterCountries(val, update) {
            update(() => {
                const needle = val.toLowerCase()
                this.filteredCountries = this.countries.filter(
                    (v) => v.name.toLowerCase().indexOf(needle) > -1
                )
            })
        },

        filterCompanyCountries(val, update) {
            update(() => {
                const needle = val.toLowerCase()
                this.filteredCompanyCountries = this.countries.filter(
                    (v) => v.name.toLowerCase().indexOf(needle) > -1
                )
            })
        },

        async fetchCountries() {
            this.loadingAdd(loadingKeys.fetchingCountries)

            let parameters = {
                filter: {},
            }

            let response = await countriesService.retrieve(parameters)

            if (response.status === 200) {
                this.countries = response.data.results
                this.filteredCountries = response.data.results
            } else {
                this.notifyError(response)
            }
            this.loadingRemove(loadingKeys.fetchingCountries)
        },

        async fetchCurrencies() {
            this.loadingAdd(loadingKeys.fetchingCurrencies)

            let parameters = {
                filter: {},
            }

            let response = await currenciesService.retrieve(parameters)

            if (response.status === 200) {
                this.currencyOptions = response.data.results
            } else {
                this.notifyError(response)
            }

            this.loadingRemove(loadingKeys.fetchingCurrencies)
        },

        resetDataAndValidations() {
            this.user = {
                first_name: '',
                last_name: '',
                tax_number: '',
                phone: '',
                street: '',
                city: '',
                state: '',
                zip_code: '',
                email: '',
                password: '',
                passwordConfirm: '',
                country_id: null,
            }
            this.company = {
                title: '',
                tax_number: '',
                phone: '',
                email: '',
                street: '',
                city: '',
                state: '',
                zip_code: '',
                country_id: null,
            }
            this.selectedCurrency = null
            this.$v.$reset()
        },

        async signUp() {
            let self = this
            if (this.userType == 'client') {
                this.$v.$touch()
                if (this.$v.$invalid || this.isSigningUp) {
                    return true
                }
            } else {
                this.$v.user.$touch()
                if (this.$v.user.$invalid || this.isSigningUp) {
                    return true
                }
            }

            this.loadingAdd(loadingKeys.signingUp)

            let parameters = {
                email: this.user.email,
                password: this.user.password,
                first_name: this.user.first_name,
                last_name: this.user.last_name,
                tax_number: this.user.tax_number,
                userrole: self.userType,
                street: this.user.street,
                city: this.user.city,
                state: this.user.state,
                zip_code: this.user.zip_code,
                country_id: this.user.country_id,
                currency: this.selectedCurrency,
            }

            if (this.userType == 'client') {
                parameters.company_title = this.company.title
                parameters.company_tax_number = this.company.tax_number
                parameters.company_email = this.company.email
                parameters.company_phone = this.company.phone
                parameters.company_street = this.company.street
                parameters.company_city = this.company.city
                parameters.company_state = this.company.state
                parameters.company_zip_code = this.company.zip_code
                parameters.company_country_id = this.company.country_id
            }

            let response = await authenticationService.signup({
                data: [parameters],
            })

            if (response.status === 200) {
                this.$router.push({ name: 'Login' })
                this.$q.notify({
                    type: 'positive',
                    message: this.$t('notify.signup_with_success'),
                })
            } else {
                this.$q.notify({
                    type: 'negative',
                    message: this.$t('errors.signup_error'),
                })
            }

            this.loadingRemove(loadingKeys.signingUp)
        },
    },
}
</script>
